import { useEffect, useState } from "react";
import { apiDelete, apiGet, apiPost, apiPut } from "../lib/api";
import { Btn, Card, Pill, Sub, Th, Td, relativeTime } from "../components/ui";

type Token = {
  id: number;
  label: string;
  region_id: number | null;
  region_name: string | null;
  created_by_name: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
  expires_at: string | null;
};
type McpUser = { id: number; name: string; role: string; region_name: string };
type ToolRow = { tool: string; calls: number; p50: number | null; enabled: boolean };

export default function McpScreen() {
  const [tokens, setTokens] = useState<Token[]>([]);
  const [tools, setTools] = useState<ToolRow[]>([]);
  const [minted, setMinted] = useState<{ url: string; label: string } | null>(null);
  const [mintOpen, setMintOpen] = useState(false);
  const [mintLabel, setMintLabel] = useState("");
  const [users, setUsers] = useState<McpUser[]>([]);
  const [mintFor, setMintFor] = useState<number | "">("");
  const [instrOpen, setInstrOpen] = useState(false);
  const [instrText, setInstrText] = useState("");
  const [error, setError] = useState<string | null>(null);

  function load() {
    apiGet<{ tokens: Token[] }>("/api/admin/mcp/tokens").then((r) => setTokens(r.tokens));
    apiGet<{ tools: ToolRow[] }>("/api/admin/mcp/tools").then((r) => setTools(r.tools));
    apiGet<{ users: McpUser[] }>("/api/admin/mcp/users").then((r) => setUsers(r.users));
  }
  useEffect(load, []);

  async function mint(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const r = await apiPost<{ url: string; label: string }>("/api/admin/mcp/tokens", {
        label: mintLabel,
        // Blank = the admin minting it. The token sees what this user sees.
        ...(mintFor ? { createdBy: mintFor } : {}),
      });
      setMinted(r);
      setMintOpen(false);
      setMintLabel("");
      setMintFor("");
      load();
    } catch (e: any) {
      setError(String(e.message ?? e));
    }
  }

  async function revoke(id: number) {
    await apiDelete(`/api/admin/mcp/tokens/${id}`);
    load();
  }

  async function toggleTool(tool: string, enabled: boolean) {
    setTools((t) => t.map((r) => (r.tool === tool ? { ...r, enabled } : r)));
    await apiPut(`/api/admin/mcp/tools/${encodeURIComponent(tool)}`, { enabled });
  }

  async function openInstructions() {
    const r = await apiGet<{ current: { text: string } | null }>("/api/admin/mcp/instructions");
    setInstrText(r.current?.text ?? "");
    setInstrOpen(true);
  }

  async function saveInstructions(e: React.FormEvent) {
    e.preventDefault();
    await apiPut("/api/admin/mcp/instructions", { text: instrText });
    setInstrOpen(false);
  }

  const endpoint = `${location.origin}/mcp/`;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl text-ink">MCP</h1>
        <Pill tone="ok">healthy</Pill>
        <span className="text-sm text-neutral-400">Streamable HTTP · token in the URL · no OAuth</span>
      </div>
      <div className="break-all rounded-lg border border-neutral-200 bg-white px-4 py-3 font-mono text-[13px] text-ink shadow-sm">
        {endpoint}
        <b>&lt;token&gt;</b>
      </div>

      {error && <p className="text-sm text-brand">{error}</p>}
      {minted && (
        <Card className="border-green-200 bg-green-50/40">
          <p className="text-sm text-ink">
            Minted <b>{minted.label}</b> — copy this now, it is shown once:
          </p>
          <code className="mt-1 block break-all rounded-md bg-white p-2 text-xs">{minted.url}</code>
          <button className="mt-2 text-xs text-neutral-500" onClick={() => setMinted(null)}>
            dismiss
          </button>
        </Card>
      )}

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3.5 lg:grid-cols-2">
        <Card>
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <h2 className="text-lg">Tokens</h2>
            <Sub>one per person · scope = what they see in the apps</Sub>
          </div>
          <div className="overflow-x-auto">
          <table className="w-full min-w-[420px]">
            <thead>
              <tr>
                <Th>token</Th>
                <Th>sees</Th>
                <Th>last call</Th>
                <Th right>7 days</Th>
                <Th> </Th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((t) => (
                <tr key={t.id}>
                  <Td>{t.label}{t.created_by_name ? ` · ${t.created_by_name}` : ""}</Td>
                  <Td>{t.region_name ?? "region"}</Td>
                  <Td>{t.revoked_at ? "revoked" : relativeTime(t.last_used_at)}</Td>
                  <Td right>—</Td>
                  <Td>
                    {!t.revoked_at && (
                      <button className="min-h-[44px] px-2 text-xs text-brand" onClick={() => revoke(t.id)}>
                        revoke
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <div className="mt-4 flex flex-wrap gap-2.5">
            <Btn onClick={() => setMintOpen(true)}>Mint token</Btn>
            <Btn secondary onClick={openInstructions}>
              Edit instructions
            </Btn>
          </div>
          <p className="mt-3.5 text-xs text-neutral-400">
            Instructions are the text Claude reads at connect. Edited here, versioned, no deploy.
          </p>
        </Card>

        <Card>
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <h2 className="text-lg">Tools</h2>
            <Sub>calls in the last 7 days</Sub>
          </div>
          <div className="max-h-[420px] overflow-y-auto overflow-x-auto">
            <table className="w-full min-w-[360px]">
              <thead>
                <tr>
                  <Th>tool</Th>
                  <Th right>calls</Th>
                  <Th right>p50</Th>
                  <Th>on</Th>
                </tr>
              </thead>
              <tbody>
                {tools.map((t) => (
                  <tr key={t.tool}>
                    <Td>{t.tool}</Td>
                    <Td right>{t.calls}</Td>
                    <Td right>{t.p50 != null ? `${t.p50} ms` : "—"}</Td>
                    <Td>
                      <button className="flex min-h-[44px] items-center" onClick={() => toggleTool(t.tool, !t.enabled)}>
                        <Pill tone={t.enabled ? "ok" : "grey"}>{t.enabled ? "on" : "off"}</Pill>
                      </button>
                    </Td>
                  </tr>
                ))}
                {tools.length === 0 && (
                  <tr>
                    <Td>No calls in the last 7 days.</Td>
                    <Td right> </Td>
                    <Td right> </Td>
                    <Td> </Td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {mintOpen && (
        <div className="fixed inset-0 z-10 flex items-center justify-center bg-black/30 p-4 sm:p-6" onClick={() => setMintOpen(false)}>
          <form onSubmit={mint} className="max-h-[85vh] w-full max-w-sm overflow-y-auto rounded-card bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg text-ink">Mint token</h3>
            <label className="mt-3 flex flex-col gap-1 text-sm font-medium text-neutral-700">
              Label
              <input
                value={mintLabel}
                onChange={(e) => setMintLabel(e.target.value)}
                required
                className="rounded-lg border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-brand"
              />
            </label>
            <label className="mt-3 flex flex-col gap-1 text-sm font-medium text-neutral-700">
              For
              <select
                value={mintFor}
                onChange={(e) => setMintFor(e.target.value ? Number(e.target.value) : "")}
                className="rounded-lg border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-brand"
              >
                <option value="">Me</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} · {u.role} · {u.region_name}
                  </option>
                ))}
              </select>
              <span className="text-xs font-normal text-neutral-400">It sees exactly what this person sees in the apps.</span>
            </label>
            <div className="mt-4 flex gap-2.5">
              <Btn type="submit">Mint</Btn>
              <Btn secondary onClick={() => setMintOpen(false)}>
                Cancel
              </Btn>
            </div>
          </form>
        </div>
      )}

      {instrOpen && (
        <div className="fixed inset-0 z-10 flex items-center justify-center bg-black/30 p-4 sm:p-6" onClick={() => setInstrOpen(false)}>
          <form
            onSubmit={saveInstructions}
            className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-card bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg text-ink">Instructions</h3>
            <textarea
              value={instrText}
              onChange={(e) => setInstrText(e.target.value)}
              rows={10}
              className="mt-3 w-full rounded-lg border border-neutral-300 p-3 text-sm outline-none focus:border-brand"
            />
            <div className="mt-4 flex gap-2.5">
              <Btn type="submit">Save new version</Btn>
              <Btn secondary onClick={() => setInstrOpen(false)}>
                Cancel
              </Btn>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
